import { Navigate, Route, Routes, useParams } from "react-router";

import NotFoundPage from "components/error/NotFoundPage";
import Layout from "components/Layout";
import useFavicon from "hooks/useFavicon";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import useIsFlowsAacIntegrationEnabled from "hooks/useIsFlowsAacIntegrationEnabled";
import { AccountContext } from "views/AccountWrapper";
import Stack from "views/Stack";
import useMainNavigationDefaultView from "components/MainNavigation/hooks/useDefaultView";
import useIsOnlySpaceAdmin from "shared/Space/useIsOnlySpaceAdmin";
import useAacFlags from "hooks/useAacFlags";
import { getRepoRevisionUrl, REPOS_PAGE_URL } from "utils/spaceliftRepo";
import FlowsLinkOrganizations from "views/Flows/LinkOrganizations";
import FlowsLogin from "views/Flows/Login";
import { ASSISTANT_PAGE_PATH } from "components/AssistantPanel/constants";

import BlueprintPage from "./Blueprint";
import Blueprints from "./Blueprints";
import BlueprintCreateStackModal from "./Blueprints/CreateStackModal";
import Context from "./Context";
import Contexts from "./Contexts";
import Flows from "./Flows";
import Dashboard from "./Dashboard";
import EditWebhook from "./EditWebhook";
import Integrations from "./Integrations";
import IntegrationDetails from "./Integrations/DetailsPage";
import IntegrationsGroup from "./Integrations/GroupPage";
import IntegrationGroupLayout from "./Integrations/components/Layouts/IntegrationGroupLayout";
import {
  INTEGRATIONS_FILTER_PATH,
  VCS_INTEGRATIONS_PATH,
} from "./Integrations/registry/shared/VcsShared/constants";
import IntentProject from "./IntentProject";
import IntentProjects from "./IntentProjects";
import LaunchPad from "./LaunchPad";
import MissionControl from "./MissionControl";
import AssistantPage from "./AssistantPage";
import LoginPolicyCreate from "./LoginPolicyCreate";
import LoginPolicyUpdate from "./LoginPolicyDetails";
import ModuleNew from "./Module";
import ModuleSettingsNew from "./ModuleSettings";
import NewContext from "./NewContext";
import NewModule from "./NewModule";
import NewPolicy from "./NewPolicy";
import NewStack from "./NewStack";
import NewWebhook from "./NewWebhook";
import Notifications from "./Notifications";
import OpenTofuMigrationQueue from "./OpenTofuMigration/MigrationQueue";
import OpenTofuMigrationReadyToMigrate from "./OpenTofuMigration/ReadyToMigrate";
import PersonalSettings from "./PersonalSettings";
import Plugins from "./Plugins/views/List";
import PluginTemplates from "./Plugins/views/Templates";
import Policies from "./Policies";
import PoliciesTemplates from "./Policies/Templates";
import PolicyDetails from "./PolicyDetails";
import Resources from "./Resources";
import Runs from "./Runs";
import ApiKey from "./Settings/ApiKey";
import { AuditTrail } from "./Settings/AuditTrail";
import AuditTrailConfig from "./Settings/AuditTrail/Config";
import AuditTrailEntries from "./Settings/AuditTrail/Entries";
import { SettingsContextProvider } from "./Settings/Context";
import IdpGroup from "./Settings/IdpGroup";
import Role from "./Settings/Role";
import SettingsRoleEditPermissions from "./Settings/Role/EditPermissions";
import Settings from "./Settings/Settings";
import User from "./Settings/User";
import SharedModulesNew from "./SharedModulesNew";
import SpaceDetails from "./SpaceDetails";
import Spaces from "./Spaces";
import StacksNew from "./Stacks";
import GpgKeys from "./TerraformRegistry/GpgKeys";
import Provider from "./TerraformRegistry/Provider";
import Providers from "./TerraformRegistry/Providers";
import VCSIntegrations from "./VCS/Integrations";
import { VCS_INTEGRATION_PATH } from "./VCS/constants";
import WorkerPool from "./WorkerPool";
import WorkerPools from "./WorkerPools";
import ModulesNew from "./ModulesNew";
import TemplatesWorkbench from "./TemplatesWorkbench";
import TemplatePage from "./Template";
import TemplateVersionPage from "./TemplateVersion";
import TemplatesMarketplace from "./TemplatesMarketplace";
import TemplatesMarketplaceTemplate from "./TemplatesMarketplace/Template";
import TemplatesMarketplaceTemplateDeployment from "./TemplatesMarketplace/TemplateDeployment";
import Deploy from "./TemplateVersion/Deploy";
import UpdateDeployment from "./TemplateVersion/UpdateDeployment";
import SpaceliftVcsRepo from "./SpaceliftVcsRepo";
import SpaceliftVcsRepoCommit from "./SpaceliftVcsRepo/Commit";
import SpaceliftVcsRepoCreateFile from "./SpaceliftVcsRepo/CreateFile";
import SpaceliftVcsRepoEditFile from "./SpaceliftVcsRepo/EditFile";
import SpaceliftVcsRepoUploadFiles from "./SpaceliftVcsRepo/UploadFiles";
import SpaceliftVcsRepos from "./SpaceliftVcsRepos";
import ScheduleScanWizard from "./NewScanSchedule";

const AccountLayoutRoutes = () => {
  const { opentofuBackend, unmanagedResourcesUi } = useTypedFlags();

  const { viewer } = useTypedContext(AccountContext);

  const { nonRootAdminRoleBindings } = useAacFlags();
  const isFlowsAacIntegrationEnabled = useIsFlowsAacIntegrationEnabled();
  const isOnlySpaceAdmin = useIsOnlySpaceAdmin();

  useFavicon();

  const { defaultViewItem } = useMainNavigationDefaultView();

  return (
    <Layout>
      <Routes>
        {defaultViewItem.to && (
          <Route index element={<Navigate to={defaultViewItem.to} replace />} />
        )}

        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/stacks" element={<StacksNew />} />

        {opentofuBackend && (
          <Route path="/opentofu-migration">
            <Route index element={<OpenTofuMigrationReadyToMigrate />} />
            <Route path="queue" element={<OpenTofuMigrationQueue />} />
          </Route>
        )}

        <Route path="/stack/:stackId">
          <Route index path="*" element={<Stack />} />
        </Route>
        <Route path="/context/:contextId">
          <Route index path="*" element={<Context />} />
        </Route>
        <Route path="/contexts" element={<Contexts />} />

        <Route path="/policies" element={<Policies />} />

        <Route path="/policies-templates" element={<PoliciesTemplates />} />

        <Route path="/policy/:policyId">
          <Route index path="*" element={<PolicyDetails />} />
        </Route>

        {viewer.admin && (
          <>
            <Route
              path="/settings/integrations/manage-slack-access"
              element={<Navigate to="/integrations/slack" replace />}
            />

            <Route path="/settings" element={<SettingsContextProvider />}>
              <Route index path="*" element={<Settings />} />

              <Route path="role/:roleId/permissions" element={<SettingsRoleEditPermissions />} />
              <Route path="role/:roleId">
                <Route index path="*" element={<Role />} />
              </Route>
              <Route path="user/:userId">
                <Route index path="*" element={<User />} />
              </Route>
              <Route path="api-key/:apiKeyId">
                <Route index path="*" element={<ApiKey />} />
              </Route>
              <Route path="idp-group/:idpGroupId">
                <Route index path="*" element={<IdpGroup />} />
              </Route>
            </Route>
          </>
        )}

        {isOnlySpaceAdmin && (
          <>
            <Route path="/settings" element={<SettingsContextProvider />}>
              <Route index path="*" element={<Settings />} />

              <Route path="role/:roleId">
                <Route index path="*" element={<Role readOnly />} />
              </Route>

              {nonRootAdminRoleBindings && (
                <>
                  <Route path="user/:userId">
                    <Route index path="*" element={<User />} />
                  </Route>

                  <Route path="idp-group/:idpGroupId">
                    <Route index path="*" element={<IdpGroup />} />
                  </Route>

                  <Route path="api-key/:apiKeyId">
                    <Route index path="*" element={<ApiKey />} />
                  </Route>
                </>
              )}
            </Route>
          </>
        )}

        <Route path="/provider/:providerId">
          <Route index path="*" element={<Provider />} />
        </Route>
        <Route path="/providers" element={<Providers />} />
        <Route path="/gpg-keys" element={<GpgKeys />} />
        <Route path="/shared-modules" element={<SharedModulesNew />} />
        <Route path="/modules" element={<ModulesNew />} />
        <Route path="/module/:moduleId/settings">
          <Route index path="*" element={<ModuleSettingsNew />} />
        </Route>
        <Route path="/module/:moduleId">
          <Route index path="*" element={<ModuleNew />} />
        </Route>
        <Route path="/new/context" element={<NewContext />} />
        <Route path="/new/module" element={<NewModule />} />
        <Route path="/new/policy" element={<NewPolicy />} />

        {viewer.admin && <Route path="/new/login-policy" element={<LoginPolicyCreate />} />}

        <Route path="/new/stack" element={<NewStack />} />
        {unmanagedResourcesUi && <Route path="/new/scan" element={<ScheduleScanWizard />} />}

        <Route path="/new/webhook" element={<NewWebhook />} />

        <Route path="/resources">
          <Route index path="*" element={<Resources />} />
        </Route>
        <Route path="/runs" element={<Runs />} />
        <Route path="/worker-pool/:workerPoolId">
          <Route index path="*" element={<WorkerPool />} />
        </Route>

        <Route path="/worker-pools" element={<WorkerPools />} />

        <Route
          path="/vcs-agent-pools"
          element={<Navigate to="/integrations/vcs-agent-pools" replace />}
        />
        <Route path="/vcs-agent-pool/:vcsAgentPoolId" element={<VCSAgentPoolRedirect />} />
        <Route path="/login-policy/:policyId">
          <Route index path="*" element={<LoginPolicyUpdate />} />
        </Route>

        <Route path="/cloud-integrations">
          <Route index path="*" element={<CloudIntegrationsRedirect />} />
        </Route>
        <Route path="/notifications" element={<Notifications />} />

        <Route path="/webhook/:webhookId/edit" element={<EditWebhook />} />
        <Route path="/webhook/:webhookId">
          <Route index path="*" element={<WebhookRedirect />} />
        </Route>

        <Route path="/webhooks" element={<Navigate to="/integrations/webhooks" />} />
        <Route path="/spaces/:spaceId">
          <Route index path="*" element={<SpaceDetails />} />
        </Route>
        <Route path="/spaces" element={<Spaces />} />

        <Route path="/blueprint/:blueprintId">
          <Route path="create" element={<BlueprintCreateStackModal />} />
          <Route index path="*" element={<BlueprintPage />} />
        </Route>
        <Route path="/blueprints" element={<Blueprints />} />

        <Route path="/templates-workbench" element={<TemplatesWorkbench />} />

        <Route path="/templates">
          <Route index element={<TemplatesMarketplace />} />
          <Route path="template/:templateId">
            <Route index element={<TemplatesMarketplaceTemplate />} />
            <Route
              path="deployment/:deploymentId"
              element={<TemplatesMarketplaceTemplateDeployment />}
            />
            <Route path="version/:versionId/deploy" element={<Deploy />} />
          </Route>
        </Route>

        <Route path="/template/:templateId">
          <Route path="version/:versionId">
            <Route path="deployments/:deploymentId/update" element={<UpdateDeployment />} />
            <Route index path="*" element={<TemplateVersionPage />} />
          </Route>
          <Route index path="*" element={<TemplatePage />} />
        </Route>

        <Route path="/personal-settings">
          <Route index path="*" element={<PersonalSettings />} />
        </Route>

        <Route path={VCS_INTEGRATIONS_PATH}>
          <Route index path="*" element={<VcsIntegrationsRedirect />} />
        </Route>

        <Route path={VCS_INTEGRATION_PATH}>
          <Route index path="*" element={<VcsIntegrationRedirect />} />
        </Route>

        <Route path="/integrations">
          <Route index element={<Integrations />} />
          <Route element={<IntegrationGroupLayout />}>
            <Route path=":integrationGroup" element={<IntegrationsGroup />} />
            <Route path=":integrationGroup/:integrationId">
              {/* handles the index and any nested URLs inside /:integrationId */}
              <Route index path="*" element={<IntegrationDetails />} />
            </Route>
          </Route>
        </Route>

        <Route path="/plugins" element={<Plugins />} />
        <Route path="/plugin-templates" element={<PluginTemplates />} />

        {isFlowsAacIntegrationEnabled && (
          <Route path="/flows">
            <Route index path="*" element={<Flows />} />
          </Route>
        )}

        <Route path={REPOS_PAGE_URL} element={<SpaceliftVcsRepos />} />
        <Route path={`${REPOS_PAGE_URL}/:repoId`}>
          <Route path="new" element={<SpaceliftVcsRepoCreateFile />} />
          <Route path="upload" element={<SpaceliftVcsRepoUploadFiles />} />
          <Route path="edit/:fileName" element={<SpaceliftVcsRepoEditFile />} />
          <Route path="commits/:sha" element={<SpaceliftVcsRepoCommit />} />
          <Route index path="*" element={<SpaceliftVcsRepo />} />
        </Route>
        <Route path="/vcs/:repoId/commit/:sha" element={<SpaceliftVcsRepoCommitRedirect />} />

        <Route path="/launchpad">
          <Route index path="*" element={<LaunchPad />} />
        </Route>

        <Route path="/mission-control">
          <Route index path="*" element={<MissionControl />} />
        </Route>

        <Route path={ASSISTANT_PAGE_PATH} element={<AssistantPage />} />

        {viewer.admin && (
          <Route path="/audit-trail" element={<AuditTrail />}>
            <Route index element={<AuditTrailEntries />} />
            <Route path="config" element={<AuditTrailConfig />} />
          </Route>
        )}

        <Route path="/intent-project/:intentProjectId">
          <Route index path="*" element={<IntentProject />} />
        </Route>
        <Route path="/intent-projects" element={<IntentProjects />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Layout>
  );
};

// Redirect component to handle webhook URL parameter passing
const WebhookRedirect = () => {
  const { webhookId } = useParams();
  return <Navigate to={`/integrations/webhooks/${webhookId}`} replace />;
};

const CloudIntegrationsRedirect = () => {
  const params = useParams();
  const childUrl = params["*"];
  return <Navigate to={`/integrations/${childUrl}`} replace />;
};

const VCSAgentPoolRedirect = () => {
  const params = useParams();
  return <Navigate to={`/integrations/vcs-agent-pools/${params.vcsAgentPoolId}`} replace />;
};

const SpaceliftVcsRepoCommitRedirect = () => {
  const { repoId = "", sha = "" } = useParams();
  return <Navigate to={getRepoRevisionUrl(repoId, sha)} replace />;
};

const VcsIntegrationsRedirect = () => {
  const params = useParams();

  if (!params["*"]) {
    return <Navigate to={INTEGRATIONS_FILTER_PATH} replace />;
  } else {
    return <VCSIntegrations />;
  }
};

const VcsIntegrationRedirect = () => {
  const params = useParams();
  return <Navigate to={`/integrations/${params.provider}/${params.id}/${params["*"]}`} replace />;
};

const Account = () => {
  return (
    <Routes>
      <Route path="/flows/link/select" element={<FlowsLinkOrganizations />} />
      <Route path="/flows/login" element={<FlowsLogin />} />
      <Route path="*" element={<AccountLayoutRoutes />} />
    </Routes>
  );
};

export default Account;
